import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserRoundPlus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { authApi, setToken, setUser, formatApiErrorDetail } from "../lib/authApi";
import { GoogleSignInButton } from "../components/GoogleSignInButton";

const initialForm = { name: "", email: "", password: "", phone: "", company_name: "" };

export default function CustomerRegister() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      toast.error("Name, email and password are required.");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await authApi.post("/auth/register", form);
      setToken(data.access_token);
      setUser(data.user);
      toast.success("Account created — welcome to Vectron!");
      navigate("/portal");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-24 px-4" data-testid="customer-register-page">
      <div className="w-full max-w-md p-8 sm:p-10 rounded-xl bg-card border border-border">
        <div className="h-12 w-12 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
          <UserRoundPlus className="h-5 w-5 text-primary" strokeWidth={1.5} />
        </div>
        <h1 className="text-2xl sm:text-3xl font-medium font-heading tracking-tight">Create Account</h1>
        <p className="mt-2 text-sm text-muted-foreground">Manage RFQs, quotes and files in one place.</p>
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="customer-register-form">
          <div className="space-y-2">
            <Label htmlFor="cr-name">Full Name *</Label>
            <Input id="cr-name" value={form.name} onChange={set("name")} placeholder="Your name" data-testid="register-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cr-email">Email *</Label>
            <Input id="cr-email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="register-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cr-password">Password * <span className="text-muted-foreground font-normal">(min 8 characters)</span></Label>
            <Input id="cr-password" type="password" value={form.password} onChange={set("password")} placeholder="••••••••" data-testid="register-password-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cr-phone">Phone</Label>
              <Input id="cr-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..." data-testid="register-phone-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cr-company">Company</Label>
              <Input id="cr-company" value={form.company_name} onChange={set("company_name")} placeholder="Company" data-testid="register-company-input" />
            </div>
          </div>
          <Button type="submit" size="lg" disabled={loading} className="w-full rounded-full" data-testid="register-submit-btn">
            {loading ? "Creating account..." : "Create Account"}
          </Button>
        </form>
        <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="h-px flex-1 bg-border" />or<span className="h-px flex-1 bg-border" />
        </div>
        <GoogleSignInButton testId="google-signin-btn-register" />
        <p className="mt-6 text-sm text-muted-foreground text-center">
          Already registered?{" "}
          <Link to="/portal/login" className="text-primary font-medium hover:underline" data-testid="goto-login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
